import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

// Separate component for game rendering to properly use React hooks
const GameRenderer = ({ content }) => {
  // Handle both old and new data structures for backward compatibility
  // New structure: content.content_data = {gameType, title, description, gameData}
  // Old structure: content.content_data might have gameData nested or at root
  const contentData = content?.content_data || {};
  
  // Try to extract gameType from multiple possible locations
  const gameType = contentData?.gameType || contentData?.game_type;
  
  // Try to extract gameData - it might be nested or the contentData itself might contain the game properties
  let gameData = contentData?.gameData || contentData?.game_data;
  
  // If gameData is not found, check if the game properties are directly in contentData
  if (!gameData || Object.keys(gameData)?.length === 0) {
    // Extract game-specific properties from contentData itself
    gameData = {
      pairs: contentData?.pairs,
      words: contentData?.words,
      gridSize: contentData?.gridSize || contentData?.grid_size,
      items: contentData?.items,
      statement: contentData?.statement,
      answer: contentData?.answer,
      blanks: contentData?.blanks,
      categories: contentData?.categories
    };
  }
  
  const title = contentData?.title || 'Interactive Game';
  const description = contentData?.description || '';

  const [gameState, setGameState] = useState({
    selectedAnswers: {},
    draggedItems: [],
    userInput: {},
    categoryAssignments: {},
    flippedCards: [],
    matchedPairs: [],
    foundWords: [],
    selectedCells: [],
    currentSelection: [],
    isComplete: false
  });

  const [draggedItem, setDraggedItem] = useState(null);
  const [dragDropOrder, setDragDropOrder] = useState([]);

  // Generate word search grid
  const wordSearchGrid = useMemo(() => {
    if (gameType !== 'word-search' || !gameData?.words) return null;
    
    const words = gameData?.words || [];
    const gridSizeStr = gameData?.gridSize || '10x10';
    const [rows, cols] = gridSizeStr?.split('x')?.map(Number);
    
    // Initialize empty grid
    const grid = Array(rows)?.fill(null)?.map(() => Array(cols)?.fill(''));
    let placed = [];
    
    // Helper to check if word can be placed
    const canPlaceWord = (word, row, col, direction) => {
      const len = word?.length;
      if (direction === 'horizontal') {
        if (col + len > cols) return false;
        for (let i = 0; i < len; i++) {
          if (grid?.[row]?.[col + i] !== '' && grid?.[row]?.[col + i] !== word?.[i]) return false;
        }
        return true;
      } else if (direction === 'vertical') {
        if (row + len > rows) return false;
        for (let i = 0; i < len; i++) {
          if (grid?.[row + i]?.[col] !== '' && grid?.[row + i]?.[col] !== word?.[i]) return false;
        }
        return true;
      } else if (direction === 'diagonal') {
        if (row + len > rows || col + len > cols) return false;
        for (let i = 0; i < len; i++) {
          if (grid?.[row + i]?.[col + i] !== '' && grid?.[row + i]?.[col + i] !== word?.[i]) return false;
        }
        return true;
      }
      return false;
    };
    
    // Place word in grid
    const placeWord = (word, row, col, direction) => {
      const positions = [];
      for (let i = 0; i < word?.length; i++) {
        if (direction === 'horizontal') {
          grid[row][col + i] = word?.[i];
          positions?.push({ row, col: col + i });
        } else if (direction === 'vertical') {
          grid[row + i][col] = word?.[i];
          positions?.push({ row: row + i, col });
        } else if (direction === 'diagonal') {
          grid[row + i][col + i] = word?.[i];
          positions?.push({ row: row + i, col: col + i });
        }
      }
      placed?.push({ word, positions });
    };
    
    // Try to place each word
    words?.forEach(word => {
      const upperWord = word?.toUpperCase();
      const directions = ['horizontal', 'vertical', 'diagonal'];
      let wordPlaced = false;
      let attempts = 0;
      
      while (!wordPlaced && attempts < 100) {
        const direction = directions?.[Math.floor(Math.random() * directions?.length)];
        const row = Math.floor(Math.random() * rows);
        const col = Math.floor(Math.random() * cols);
        
        if (canPlaceWord(upperWord, row, col, direction)) {
          placeWord(upperWord, row, col, direction);
          wordPlaced = true;
        }
        attempts++;
      }
    });
    
    // Fill empty cells with random letters
    const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    for (let i = 0; i < rows; i++) {
      for (let j = 0; j < cols; j++) {
        if (grid?.[i]?.[j] === '') {
          grid[i][j] = letters?.[Math.floor(Math.random() * letters?.length)];
        }
      }
    }
    
    return { grid, placed };
  }, [gameType, gameData?.words, gameData?.gridSize]);

  // Shuffle cards for memory match (memoized)
  const shuffledCards = useMemo(() => {
    if (gameType !== 'memory-match' || !gameData?.pairs) return [];
    const pairs = gameData?.pairs || [];
    const allCards = [
      ...pairs?.map((p, i) => ({ type: 'term', value: p?.term, pairId: i })),
      ...pairs?.map((p, i) => ({ type: 'definition', value: p?.definition, pairId: i }))
    ];
    return allCards?.sort(() => Math.random() - 0.5);
  }, [gameType, gameData?.pairs]);

  // Initialize drag-drop order
  useMemo(() => {
    if (gameType === 'drag-drop' && gameData?.items && dragDropOrder?.length === 0) {
      setDragDropOrder([...gameData?.items]?.sort(() => Math.random() - 0.5));
    }
  }, [gameType, gameData?.items, dragDropOrder?.length]);

  const handleWordSearchCellClick = (row, col) => {
    const cellKey = `${row}-${col}`;
    const newSelection = [...gameState?.currentSelection];
    
    if (newSelection?.some(cell => cell?.key === cellKey)) {
      // Remove from selection
      const filtered = newSelection?.filter(cell => cell?.key !== cellKey);
      setGameState(prev => ({ ...prev, currentSelection: filtered }));
    } else {
      // Add to selection
      newSelection?.push({ row, col, key: cellKey, letter: wordSearchGrid?.grid?.[row]?.[col] });
      setGameState(prev => ({ ...prev, currentSelection: newSelection }));
    }
  };

  const checkWordSearchSelection = () => {
    const selectedWord = gameState?.currentSelection?.map(cell => cell?.letter)?.join('');
    const words = gameData?.words?.map(w => w?.toUpperCase()) || [];
    
    if (words?.includes(selectedWord) && !gameState?.foundWords?.includes(selectedWord)) {
      setGameState(prev => ({
        ...prev,
        foundWords: [...prev?.foundWords, selectedWord],
        selectedCells: [...prev?.selectedCells, ...prev?.currentSelection?.map(c => c?.key)],
        currentSelection: []
      }));
    } else {
      setGameState(prev => ({ ...prev, currentSelection: [] }));
    }
  };

  const checkMemoryMatch = (index1, index2) => {
    if (shuffledCards?.[index1]?.pairId === shuffledCards?.[index2]?.pairId) {
      setGameState(prev => ({
        ...prev,
        matchedPairs: [...prev?.matchedPairs, shuffledCards?.[index1]?.pairId],
        flippedCards: []
      }));
    } else {
      setTimeout(() => {
        setGameState(prev => ({ ...prev, flippedCards: [] }));
      }, 1000);
    }
  };

  const handleCardFlip = (index) => {
    if (gameState?.flippedCards?.length === 2 || gameState?.flippedCards?.includes(index)) return;
    
    const newFlipped = [...gameState?.flippedCards, index];
    setGameState(prev => ({ ...prev, flippedCards: newFlipped }));
    
    if (newFlipped?.length === 2) {
      checkMemoryMatch(newFlipped?.[0], newFlipped?.[1]);
    }
  };

  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, dropIndex) => {
    e?.preventDefault();
    if (draggedItem === null) return;
    
    const newOrder = [...dragDropOrder];
    const draggedElement = newOrder?.[draggedItem];
    newOrder?.splice(draggedItem, 1);
    newOrder?.splice(dropIndex, 0, draggedElement);
    
    setDragDropOrder(newOrder);
    setDraggedItem(null);
  };

  const checkDragDropOrder = () => {
    // Check if items are in correct order - handle both 'order' and 'correctPosition' properties
    const correct = dragDropOrder?.every((item, idx) => {
      const correctPos = item?.correctPosition !== undefined ? item?.correctPosition : item?.order;
      return correctPos === idx + 1 || correctPos === idx;
    });
    setGameState(prev => ({ ...prev, isComplete: correct }));
  };

  const handleTrueFalseAnswer = (answer) => {
    const isCorrect = answer === gameData?.answer;
    setGameState(prev => ({ ...prev, selectedAnswers: { trueFalse: answer }, isComplete: isCorrect }));
  };

  const handleFillBlankInput = (index, value) => {
    setGameState(prev => ({
      ...prev,
      userInput: { ...prev?.userInput, [index]: value }
    }));
  };

  const checkFillBlanks = () => {
    const blanks = gameData?.blanks || [];
    let allCorrect = blanks?.every((blank, index) => 
      gameState?.userInput?.[index]?.toLowerCase()?.trim() === blank?.answer?.toLowerCase()?.trim()
    );
    setGameState(prev => ({ ...prev, isComplete: allCorrect }));
  };

  const handleSortingDrop = (item, categoryIndex) => {
    setGameState(prev => ({
      ...prev,
      categoryAssignments: { ...prev?.categoryAssignments, [item]: categoryIndex }
    }));
  };

  const checkSorting = () => {
    const categories = gameData?.categories || [];
    let allCorrect = true;
    categories?.forEach((category, catIndex) => {
      category?.items?.forEach(item => {
        const key = item && typeof item === 'object' ? JSON.stringify(item) : item;
        if (gameState?.categoryAssignments?.[key] !== catIndex) {
          allCorrect = false;
        }
      });
    });
    setGameState(prev => ({ ...prev, isComplete: allCorrect }));
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center space-x-2">
        <Icon name="Gamepad2" size={20} className="text-primary" />
        <div>
          <h3 className="text-lg font-semibold text-foreground">{title}</h3>
          {description && <p className="text-xs text-muted-foreground">{description}</p>}
        </div>
      </div>
      <div className="pl-7">
        {gameType === 'memory-match' && (
          <div className="space-y-4">
            <div className="grid grid-cols-4 gap-3 max-w-2xl">
              {shuffledCards?.map((card, index) => {
                const isFlipped = gameState?.flippedCards?.includes(index);
                const isMatched = gameState?.matchedPairs?.includes(card?.pairId);

                return (
                  <button
                    key={index}
                    onClick={() => !isMatched && handleCardFlip(index)}
                    disabled={isMatched}
                    className={`aspect-square p-4 rounded-lg border-2 transition-all ${
                      isMatched
                        ? 'bg-green-100 border-green-500 cursor-not-allowed'
                        : isFlipped
                        ? 'bg-primary/10 border-primary' :'bg-card border-border hover:border-primary'
                    }`}
                  >
                    <div className="text-sm text-center">
                      {isFlipped || isMatched ? (
                        card?.value && typeof card?.value === 'object' ? (
                          card?.value?.itemType === 'image' && card?.value?.itemImage ? (
                            <img src={card?.value?.itemImage} alt={card?.value?.text || 'card'} className="w-full h-full object-cover rounded" />
                          ) : (
                            card?.value?.text || ''
                          )
                        ) : (
                          card?.value
                        )
                      ) : '?'}
                    </div>
                  </button>
                );
              })}
            </div>
            {gameState?.matchedPairs?.length > 0 && gameState?.matchedPairs?.length === (gameData?.pairs?.length || 0) && (
              <div className="text-center text-success font-medium">
                <Icon name="CheckCircle2" size={20} className="inline mr-2" />
                All pairs matched! Great job!
              </div>
            )}
          </div>
        )}
        {gameType === 'word-search' && wordSearchGrid && (
          <div className="space-y-4">
            <div className="bg-muted/30 p-4 rounded-lg">
              <h4 className="font-medium text-foreground mb-2">Words to Find:</h4>
              <div className="flex flex-wrap gap-2">
                {gameData?.words?.map((word, index) => (
                  <span
                    key={index}
                    className={`px-3 py-1 rounded-full text-sm ${
                      gameState?.foundWords?.includes(word?.toUpperCase())
                        ? 'bg-green-100 text-green-700 line-through' :'bg-card border border-border text-foreground'
                    }`}
                  >
                    {word}
                  </span>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 border-2 border-border max-w-fit mx-auto">
              <div className="grid gap-1" style={{ gridTemplateColumns: `repeat(${wordSearchGrid?.grid?.[0]?.length}, minmax(0, 1fr))` }}>
                {wordSearchGrid?.grid?.map((row, rowIndex) => 
                  row?.map((letter, colIndex) => {
                    const cellKey = `${rowIndex}-${colIndex}`;
                    const isSelected = gameState?.currentSelection?.some(cell => cell?.key === cellKey);
                    const isFound = gameState?.selectedCells?.includes(cellKey);
                    
                    return (
                      <button
                        key={cellKey}
                        onClick={() => handleWordSearchCellClick(rowIndex, colIndex)}
                        className={`w-8 h-8 flex items-center justify-center text-sm font-semibold rounded transition-all ${
                          isFound
                            ? 'bg-green-200 text-green-800'
                            : isSelected
                            ? 'bg-primary text-white' :'bg-gray-100 hover:bg-gray-200 text-gray-700'
                        }`}
                      >
                        {letter}
                      </button>
                    );
                  })
                )}
              </div>
            </div>
            {gameState?.currentSelection?.length > 0 && (
              <div className="flex justify-center gap-2">
                <Button onClick={checkWordSearchSelection} iconName="Check" size="sm">
                  Check Word
                </Button>
                <Button 
                  onClick={() => setGameState(prev => ({ ...prev, currentSelection: [] }))}
                  variant="outline"
                  iconName="X"
                  size="sm"
                >
                  Clear
                </Button>
              </div>
            )}
            {gameState?.foundWords?.length === gameData?.words?.length && (
              <div className="text-center text-success font-medium">
                <Icon name="CheckCircle2" size={20} className="inline mr-2" />
                All words found! Excellent!
              </div>
            )}
          </div>
        )}
        {gameType === 'drag-drop' && (
          <div className="space-y-4 max-w-2xl">
            <p className="text-sm text-muted-foreground">Drag items to arrange them in the correct order:</p>
            <div className="space-y-2">
              {dragDropOrder?.map((item, index) => (
                <div
                  key={index}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, index)}
                  className={`p-4 bg-card border-2 rounded-lg cursor-move hover:border-primary transition-all ${
                    draggedItem === index ? 'opacity-50 border-primary' : 'border-border'
                  }`}
                >
                  <div className="flex items-center">
                    <Icon name="GripVertical" size={20} className="text-muted-foreground mr-3" />
                    {item && typeof item === 'object' ? (
                      item?.itemType === 'image' && item?.itemImage ? (
                        <img src={item?.itemImage} alt={item?.text || 'item'} className="h-16 w-16 object-cover rounded" />
                      ) : (
                        <span className="text-foreground">{item?.text || ''}</span>
                      )
                    ) : (
                      <span className="text-foreground">{item?.text ?? item}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <Button onClick={checkDragDropOrder} iconName="Check" fullWidth>
              Check Order
            </Button>
            {gameState?.isComplete !== undefined && (
              <div className={`text-center font-medium ${
                gameState?.isComplete ? 'text-success' : 'text-destructive'
              }`}>
                <Icon
                  name={gameState?.isComplete ? 'CheckCircle2' : 'XCircle'}
                  size={20}
                  className="inline mr-2"
                />
                {gameState?.isComplete ? 'Perfect order!' : 'Not quite right. Try again!'}
              </div>
            )}
          </div>
        )}
        {gameType === 'true-false' && (
          <div className="space-y-4 max-w-2xl">
            <div className="p-6 bg-muted/30 rounded-lg">
              <p className="text-lg text-foreground text-center">{gameData?.statement}</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Button
                variant={gameState?.selectedAnswers?.trueFalse === 'true' ? 'default' : 'outline'}
                onClick={() => handleTrueFalseAnswer('true')}
                iconName="Check"
                fullWidth
              >
                True
              </Button>
              <Button
                variant={gameState?.selectedAnswers?.trueFalse === 'false' ? 'default' : 'outline'}
                onClick={() => handleTrueFalseAnswer('false')}
                iconName="X"
                fullWidth
              >
                False
              </Button>
            </div>
            {gameState?.selectedAnswers?.trueFalse && (
              <div className={`text-center font-medium ${
                gameState?.isComplete ? 'text-success' : 'text-destructive'
              }`}>
                <Icon
                  name={gameState?.isComplete ? 'CheckCircle2' : 'XCircle'}
                  size={20}
                  className="inline mr-2"
                />
                {gameState?.isComplete ? 'Correct!' : 'Incorrect. Try again!'}
              </div>
            )}
          </div>
        )}
        {gameType === 'fill-blanks' && (
          <div className="space-y-4 max-w-2xl">
            {gameData?.blanks?.map((blank, index) => (
              <div key={index} className="p-4 bg-muted/30 rounded-lg space-y-3">
                <p className="text-foreground">
                  {blank?.sentence?.split('_____')?.map((part, i, arr) => (
                    <React.Fragment key={i}>
                      {part}
                      {i < arr?.length - 1 && (
                        <input
                          type="text"
                          value={gameState?.userInput?.[index] || ''}
                          onChange={(e) => handleFillBlankInput(index, e?.target?.value)}
                          className="inline-block w-32 px-2 py-1 mx-1 border-b-2 border-primary bg-transparent text-center focus:outline-none"
                          placeholder="?"
                        />
                      )}
                    </React.Fragment>
                  ))}
                </p>
              </div>
            ))}
            <Button onClick={checkFillBlanks} iconName="Check" fullWidth>
              Check Answers
            </Button>
            {gameState?.isComplete !== undefined && (
              <div className={`text-center font-medium ${
                gameState?.isComplete ? 'text-success' : 'text-destructive'
              }`}>
                <Icon
                  name={gameState?.isComplete ? 'CheckCircle2' : 'XCircle'}
                  size={20}
                  className="inline mr-2"
                />
                {gameState?.isComplete ? 'All correct!' : 'Some answers are incorrect. Try again!'}
              </div>
            )}
          </div>
        )}
        {gameType === 'sorting' && (
          <div className="space-y-4 max-w-3xl">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {gameData?.categories?.map((category, catIndex) => (
                <div key={catIndex} className="p-4 bg-muted/30 rounded-lg">
                  <h4 className="font-medium text-foreground mb-3">{category?.name}</h4>
                  <div className="space-y-2 min-h-[100px] border-2 border-dashed border-border rounded-lg p-2">
                    {Object.entries(gameState?.categoryAssignments)?.filter(([_, assignedCat]) => assignedCat === catIndex)?.map(([itemKey]) => {
                        let displayContent;
                        try {
                          const parsed = JSON.parse(itemKey);
                          if (parsed && typeof parsed === 'object') {
                            displayContent = parsed?.itemType === 'image' && parsed?.itemImage ? (
                              <img src={parsed?.itemImage} alt={parsed?.text || 'item'} className="h-12 w-12 object-cover rounded" />
                            ) : (
                              parsed?.text || itemKey
                            );
                          } else {
                            displayContent = itemKey;
                          }
                        } catch {
                          displayContent = itemKey;
                        }
                        return (
                          <div key={itemKey} className="p-2 bg-card border border-border rounded text-sm">
                            {displayContent}
                          </div>
                        );
                      })}
                  </div>
                </div>
              ))}
            </div>
            <div className="p-4 bg-card border border-border rounded-lg">
              <h4 className="font-medium text-foreground mb-3">Items to Sort:</h4>
              <div className="flex flex-wrap gap-2">
                {gameData?.categories?.flatMap(cat => cat?.items || [])?.filter(item => {
                    const key = item && typeof item === 'object' ? JSON.stringify(item) : item;
                    return !gameState?.categoryAssignments?.[key];
                  })?.map((item, index) => {
                    const key = item && typeof item === 'object' ? JSON.stringify(item) : item;
                    const isImage = item && typeof item === 'object' && item?.itemType === 'image' && item?.itemImage;
                    return (
                      <button
                        key={index}
                        onClick={() => handleSortingDrop(key, 0)}
                        className="px-3 py-2 bg-primary/10 border border-primary rounded-lg text-sm hover:bg-primary/20 transition-all"
                      >
                        {isImage ? (
                          <img src={item?.itemImage} alt={item?.text || 'item'} className="h-12 w-12 object-cover rounded" />
                        ) : (
                          item && typeof item === 'object' ? (item?.text || '') : item
                        )}
                      </button>
                    );
                  })}
              </div>
            </div>
            <Button onClick={checkSorting} iconName="Check" fullWidth>
              Check Sorting
            </Button>
            {gameState?.isComplete !== undefined && (
              <div className={`text-center font-medium ${
                gameState?.isComplete ? 'text-success' : 'text-destructive'
              }`}>
                <Icon
                  name={gameState?.isComplete ? 'CheckCircle2' : 'XCircle'}
                  size={20}
                  className="inline mr-2"
                />
                {gameState?.isComplete ? 'Perfect sorting!' : 'Some items are in wrong categories. Try again!'}
              </div>
            )}
          </div>
        )}
        {!gameType && (
          <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-xl p-12 text-center max-w-2xl">
            <Icon name="Gamepad2" size={64} className="text-primary mx-auto mb-4" />
            <p className="text-muted-foreground mb-4">
              {description || 'Interactive game component'}
            </p>
            <div className="inline-flex items-center space-x-2 text-sm text-primary">
              <Icon name="Sparkles" size={16} />
              <span>Game will load here</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Content renderer for individual content blocks
const ContentBlock = ({ content }) => {
  switch (content?.content_type) {
    case 'text':
      return (
        <div className="space-y-3">
          {content?.content_data?.title && (
            <h3 className="text-2xl font-bold text-foreground">
              {content?.content_data?.title}
            </h3>
          )}
          <div className="text-foreground leading-relaxed whitespace-pre-wrap">
            {content?.content_data?.body}
          </div>
        </div>
      );
    
    case 'image':
      return (
        <div className="space-y-3">
          {content?.content_data?.url && (
            <div className="rounded-lg overflow-hidden bg-muted max-w-3xl mx-auto">
              <Image
                src={content?.content_data?.url}
                alt={content?.content_data?.alt || 'Trail content image'}
                className="w-full h-auto object-contain max-h-96"
              />
            </div>
          )}
          {content?.content_data?.caption && (
            <p className="text-center text-muted-foreground italic">
              {content?.content_data?.caption}
            </p>
          )}
        </div>
      );
    
    case 'video':
      return (
        <div className="space-y-3">
          {content?.content_data?.title && (
            <h3 className="text-xl font-semibold text-foreground">
              {content?.content_data?.title}
            </h3>
          )}
          {content?.content_data?.url && (() => {
            const rawUrl = content?.content_data?.url || '';
            const getEmbedUrl = (url) => {
              if (!url) return '';
              if (url?.includes('youtube.com/embed/')) return url;
              const shortMatch = url?.match(/youtu\.be\/([^?&]+)/);
              if (shortMatch) return `https://www.youtube.com/embed/${shortMatch?.[1]}`;
              const watchMatch = url?.match(/[?&]v=([^?&]+)/);
              if (watchMatch) return `https://www.youtube.com/embed/${watchMatch?.[1]}`;
              return url;
            };
            const embedUrl = getEmbedUrl(rawUrl);
            return (
              <div className="aspect-video rounded-lg overflow-hidden bg-black max-w-3xl mx-auto">
                <iframe
                  src={embedUrl}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title={content?.content_data?.title || 'Video content'}
                />
              </div>
            );
          })()}
        </div>
      );
    
    case 'quiz':
      return <QuizBlock content={content} />;
    
    case 'game':
      return <GameRenderer content={content} />;
    
    case 'completion':
      return (
        <div className="flex flex-col items-center justify-center space-y-6 py-12">
          <div className="w-24 h-24 bg-success/10 rounded-full flex items-center justify-center">
            <Icon name="Award" size={48} className="text-success" />
          </div>
          <div className="text-center space-y-3 max-w-2xl">
            <h2 className="text-3xl font-bold text-foreground">
              {content?.content_data?.title || 'Congratulations!'}
            </h2>
            <p className="text-lg text-muted-foreground">
              {content?.content_data?.message || 'You have completed this learning trail!'}
            </p>
          </div>
          <div className="flex items-center space-x-2 text-success">
            <Icon name="CheckCircle2" size={20} />
            <span className="font-medium">Trail Complete</span>
          </div>
        </div>
      );
    
    default:
      return (
        <div className="text-center py-8">
          <Icon name="FileQuestion" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">Unknown content type</p>
        </div>
      );
  }
};

// Quiz block component with state management
const QuizBlock = ({ content }) => {
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);

  const handleAnswerSelect = (index) => {
    setSelectedAnswer(index);
    setShowResult(true);
  };

  const isCorrect = selectedAnswer === content?.content_data?.correctAnswer;

  return (
    <div className="space-y-4 max-w-3xl mx-auto">
      <div className="flex items-start space-x-3">
        <div className="w-10 h-10 bg-orange-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
          <Icon name="HelpCircle" size={20} className="text-orange-500" />
        </div>
        <div className="flex-1">
          <h3 className="text-xl font-semibold text-foreground mb-4">
            {content?.content_data?.question}
          </h3>
          <div className="space-y-3">
            {content?.content_data?.options?.map((option, index) => {
              const isSelected = selectedAnswer === index;
              const isCorrectAnswer = index === content?.content_data?.correctAnswer;
              
              return (
                <button
                  key={index}
                  onClick={() => !showResult && handleAnswerSelect(index)}
                  disabled={showResult}
                  className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                    showResult
                      ? isCorrectAnswer
                        ? 'bg-success/10 border-success'
                        : isSelected
                        ? 'bg-destructive/10 border-destructive' :'bg-card border-border opacity-50'
                      : isSelected
                      ? 'bg-primary/10 border-primary' :'bg-card border-border hover:border-primary'
                  } ${
                    showResult ? 'cursor-not-allowed' : 'cursor-pointer'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-foreground">{option}</span>
                    {showResult && isCorrectAnswer && (
                      <Icon name="CheckCircle2" size={20} className="text-success" />
                    )}
                    {showResult && isSelected && !isCorrectAnswer && (
                      <Icon name="XCircle" size={20} className="text-destructive" />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
          {showResult && (
            <div className={`mt-4 p-4 rounded-lg ${
              isCorrect ? 'bg-success/10' : 'bg-destructive/10'
            }`}>
              <p className={`font-medium ${
                isCorrect ? 'text-success' : 'text-destructive'
              }`}>
                {isCorrect ? '✓ Correct!' : '✗ Incorrect'}
              </p>
              {content?.content_data?.explanation && (
                <p className="text-sm text-muted-foreground mt-2">
                  {content?.content_data?.explanation}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const TrailContentSection = ({ contents }) => {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);

  // Group content by pages
  const slides = useMemo(() => {
    const pageMap = {};
    
    contents?.forEach(item => {
      const pageNum = item?.page_number || 1;
      if (!pageMap?.[pageNum]) {
        pageMap[pageNum] = [];
      }
      pageMap?.[pageNum]?.push(item);
    });

    // Convert to array and sort by page number
    return Object.keys(pageMap)?.map(Number)?.sort((a, b) => a - b)?.map(pageNum => ({
        pageNumber: pageNum,
        contents: pageMap?.[pageNum]?.sort((a, b) => a?.order_position - b?.order_position),
        isCompletionPage: pageMap?.[pageNum]?.some(item => item?.content_type === 'completion')
      }));
  }, [contents]);

  const currentSlide = slides?.[currentSlideIndex];
  const isFirstSlide = currentSlideIndex === 0;
  const isLastSlide = currentSlideIndex === slides?.length - 1;

  const handleNext = () => {
    if (!isLastSlide) {
      setCurrentSlideIndex(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (!isFirstSlide) {
      setCurrentSlideIndex(prev => prev - 1);
    }
  };

  const handleSlideSelect = (index) => {
    setCurrentSlideIndex(index);
  };

  if (!contents || contents?.length === 0) {
    return (
      <div className="bg-card rounded-lg p-8 text-center border border-border">
        <Icon name="FileQuestion" size={48} className="text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-foreground mb-2">No Content Yet</h3>
        <p className="text-sm text-muted-foreground">
          This trail doesn't have any content blocks yet.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Slide Container */}
      <div className="bg-card rounded-lg shadow-lg border border-border overflow-hidden">
        {/* Slide Header */}
        <div className="bg-primary/5 border-b border-border px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {currentSlide?.isCompletionPage ? (
                <>
                  <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
                    <Icon name="Award" size={20} className="text-success" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-foreground">Completion Page</h3>
                    <p className="text-sm text-muted-foreground">Final page of the trail</p>
                  </div>
                </>
              ) : (
                <>
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name="BookOpen" size={20} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-foreground">
                      Page {currentSlide?.pageNumber}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {currentSlide?.contents?.length} content {currentSlide?.contents?.length === 1 ? 'block' : 'blocks'}
                    </p>
                  </div>
                </>
              )}
            </div>
            <div className="text-sm text-muted-foreground">
              {currentSlideIndex + 1} / {slides?.length}
            </div>
          </div>
        </div>

        {/* Slide Content */}
        <div className="p-8 min-h-[500px] max-h-[600px] overflow-y-auto">
          <div className="space-y-8">
            {currentSlide?.contents?.map((content, idx) => (
              <div key={content?.id || idx}>
                <ContentBlock content={content} />
                {idx < currentSlide?.contents?.length - 1 && (
                  <div className="mt-8 border-t border-border" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Slide Navigation */}
        <div className="bg-muted/30 border-t border-border px-6 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              onClick={handlePrevious}
              disabled={isFirstSlide}
              iconName="ChevronLeft"
              className="min-w-[120px]"
            >
              Previous
            </Button>

            {/* Page Indicators */}
            <div className="flex items-center space-x-2">
              {slides?.map((slide, index) => (
                <button
                  key={index}
                  onClick={() => handleSlideSelect(index)}
                  className={`transition-all rounded-full ${
                    index === currentSlideIndex
                      ? 'w-8 h-3 bg-primary' :'w-3 h-3 bg-muted-foreground/30 hover:bg-muted-foreground/50'
                  }`}
                  title={`Go to page ${slide?.pageNumber}`}
                />
              ))}
            </div>

            <Button
              variant="default"
              onClick={handleNext}
              disabled={isLastSlide}
              iconName="ChevronRight"
              className="min-w-[120px]"
            >
              Next
            </Button>
          </div>
        </div>
      </div>

      {/* Progress Summary */}
      <div className="flex items-center justify-center space-x-6 text-sm text-muted-foreground">
        <div className="flex items-center space-x-2">
          <Icon name="Layers" size={16} className="text-primary" />
          <span>{slides?.length} total pages</span>
        </div>
        <div className="w-1 h-1 rounded-full bg-muted-foreground/30" />
        <div className="flex items-center space-x-2">
          <Icon name="FileText" size={16} className="text-primary" />
          <span>{contents?.length} content blocks</span>
        </div>
      </div>
    </div>
  );
};

export default TrailContentSection;